import { useMemo } from 'react';

import { useStaticQuery, graphql } from 'gatsby';

import { pageOptionType, miscOptionType, anyStringOptionType } from '@consts';

export const useProducts = (productParam = '', field = 'id') => {
  /**
   * не смотря на то, что в настройках полулейт для colorVariants и recommendations указывается только id и key,
   * graphql похоже умеет находить существующие ноды по id и выдавать полные данные
   */
  const {
    allStrapiProduct: { nodes },
  } = useStaticQuery(graphql`
    {
      allStrapiProduct {
        nodes {
          id
          key
          type
          productType
          innerType
          title
          subtitle
          description
          characteristics
          seoTitle
          seoDescription
          filters {
            id
            coverType
            productFormat
          }
          rank
          labels {
            id
            name
            text
            color
            backgroundColor
          }
          url
          video {
            localFile {
              publicURL
            }
          }
          image {
            localFile {
              publicURL
              childImageSharp {
                gatsbyImageData(
                  aspectRatio: 1
                  width: 320
                  outputPixelDensities: [1, 2]
                  transformOptions: { cropFocus: CENTER }
                )
              }
            }
          }
          actionUrl
          actionTitle
          options {
            ... on STRAPI__COMPONENT_PRODUCT_COMPONENTS_PAGE_OPTION {
              id
              strapi_component
              value
              price
              optionSalePrice
              actionUrl
              title
              productSize
            }
            ... on STRAPI__COMPONENT_PRODUCT_COMPONENTS_MISC_OPTION {
              id
              strapi_component
              title
              value
              icon {
                localFile {
                  publicURL
                }
              }
            }
            ... on STRAPI__COMPONENT_PRODUCT_COMPONENTS_ANY_STRING_OPTION {
              id
              strapi_component
              optionId
              title
              value
            }
          }
          colorVariants {
            color
            title
            product {
              url
            }
          }
          promos {
            id
            title
            type
            promocode
          }
          services {
            id
            title
            subtitle
            miniSubtitle
            color
            url
            price
            icon {
              localFile {
                publicURL
              }
            }
          }
          gallery {
            id
            localFile {
              publicURL
              full: childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH)
              }
              preview: childImageSharp {
                gatsbyImageData(
                  aspectRatio: 1
                  width: 320
                  outputPixelDensities: [1, 2]
                  transformOptions: { cropFocus: CENTER }
                )
              }
            }
          }
          recommendations {
            id
          }
          isUnavailable
          preOrder
          googleProductCategory
          fbProductCategory
          actionDisabled
        }
      }
    }
  `);
  const transformedNodes = nodes
    .map((node) => {
      const transformedNode = JSON.parse(JSON.stringify(node));
      const pageOptions =
        (node.options &&
          node.options.filter((option) => option.strapi_component === pageOptionType)) ||
        [];
      const anyStringOptions =
        (node.options &&
          node.options.filter((option) => option.strapi_component === anyStringOptionType)) ||
        [];
      const miscOptions =
        (node.options &&
          node.options.filter((option) => option.strapi_component === miscOptionType)) ||
        [];
      transformedNode.options = {
        pageOptions,
        miscOptions,
        anyStringOptions,
      };
      transformedNode.colorVariants = (node.colorVariants ?? []).sort((variantA, variantB) =>
        variantA.title.localeCompare(variantB.title)
      );
      return transformedNode;
    })
    .sort((a, b) => a.rank - b.rank);

  const photobooks = [];
  const calendarProducts = [];
  const otherProducts = [];
  const notebookProducts = [];
  const weddingProducts = [];
  const detskayaProducts = [];
  const traditionsBook = [];
  const saleProducts = [];
  const giftProducts = [];
  const printsProducts = [];
  const preorderProducts = [];
  const postcardProducts = [];
  const wrappingPaperProducts = [];
  const allProducts = transformedNodes;

  transformedNodes.forEach((node) => {
    if (node.productType === 'book') {
      photobooks.push(node);
    } else if (node.productType === 'calendar') {
      calendarProducts.push(node);
    } else if (node.productType === 'wedding') {
      weddingProducts.push(node);
    } else if (node.productType === 'detskaya') {
      detskayaProducts.push(node);
    } else if (node.productType === 'notebook') {
      notebookProducts.push(node);
    } else if (node.productType === 'postcard') {
      postcardProducts.push(node);
    } else if (node.productType === 'wrappingPaper') {
      wrappingPaperProducts.push(node);
    } else if (node.productType === 'traditionsBook') {
      traditionsBook.push(node);
    } else if (node.productType === 'prints' || node.productType === 'stickers') {
      printsProducts.push(node);
    } else if (node.productType !== 'preorder' && node.productType !== 'gift') {
      otherProducts.push(node);
    } else if (node.productType === 'gift') {
      giftProducts.push(node);
    }
    if (
      node.labels?.some(
        (label) => label.name === 'sale' || label.name === 'sale1' || label.name === 'sale20'
      )
    ) {
      saleProducts.push(node);
    }
    if (node.preOrder) {
      preorderProducts.push(node);
    }
  });

  const productByParam = useMemo(
    () =>
      productParam ? transformedNodes.filter((node) => node[field] === productParam)[0] : undefined,
    // TODO: transformedNodes будут меняться на каждый рендер. Надо посмотреть внимательнее
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productParam]
  );

  return {
    photobooks,
    calendarProducts,
    otherProducts,
    notebookProducts,
    weddingProducts,
    detskayaProducts,
    traditionsBook,
    saleProducts,
    allProducts,
    giftProducts,
    printsProducts,
    preorderProducts,
    postcardProducts,
    wrappingPaperProducts,
    productByParam,
  };
};
